import type { DayEntry, DaySummary, SquareTipShareSummary } from "./types";

/**
 * Pure function: given daily tip entries, a processing fee %, and optional
 * event pool additions, compute each bartender's share of the net tip pool
 * proportional to hours worked.
 */
export function calculateDay(
  entries: DayEntry[],
  processingFeePct: number,
  eventNetToPool: number = 0,
): DaySummary {
  const totalRawTips = entries.reduce((s, e) => s + e.raw_tips, 0);
  const feeMultiplier = 1 - processingFeePct / 100;
  const processingFee = roundCents(totalRawTips * (processingFeePct / 100));
  const netRegularTips = roundCents(totalRawTips * feeMultiplier);
  const totalPool = roundCents(netRegularTips + eventNetToPool);
  const totalHours = entries.reduce((s, e) => s + e.hours_worked, 0);

  const calculated = entries.map((e) => {
    const pct = totalHours > 0 ? e.hours_worked / totalHours : 0;
    return {
      employee_id: e.employee_id,
      employee_name: e.employee_name,
      hours_worked: e.hours_worked,
      raw_tips: e.raw_tips,
      percent_of_hours: pct,
      calculated_tips: 0,
    };
  });

  if (totalHours > 0) {
    let distributed = 0;
    for (let i = 0; i < calculated.length - 1; i++) {
      calculated[i].calculated_tips = roundCents(totalPool * calculated[i].percent_of_hours);
      distributed += calculated[i].calculated_tips;
    }
    if (calculated.length > 0) {
      calculated[calculated.length - 1].calculated_tips = roundCents(totalPool - distributed);
    }
  }

  return {
    total_raw_tips: totalRawTips,
    processing_fee: processingFee,
    event_net_to_pool: eventNetToPool,
    total_pool: totalPool,
    total_hours: totalHours,
    entries: calculated,
  };
}

/**
 * Square Tip Share: pooled tips for front desk + kitchen employees,
 * split proportionally by hours worked over a pay period.
 * A processing fee (default 3%) is deducted before splitting.
 */
export function calculateSquareTipShare(
  totalTips: number,
  processingFeePct: number,
  employees: Array<{ employee_id: string; employee_name: string; hours_worked: number; extra_tips: number; event_tips: number[] }>,
): SquareTipShareSummary {
  const processingFee = roundCents(totalTips * (processingFeePct / 100));
  const netPool = roundCents(totalTips - processingFee);
  const totalHours = employees.reduce((s, e) => s + e.hours_worked, 0);
  const tipPerHour = totalHours > 0 ? netPool / totalHours : 0;

  const entries = employees.map((e) => {
    const pct = totalHours > 0 ? e.hours_worked / totalHours : 0;
    const eventTipsTotal = roundCents(e.event_tips.reduce((s, v) => s + v, 0));
    return {
      employee_id: e.employee_id,
      employee_name: e.employee_name,
      hours_worked: e.hours_worked,
      percent_of_hours: pct,
      calculated_share: 0,
      extra_tips: e.extra_tips,
      event_tips_total: eventTipsTotal,
      total: 0,
    };
  });

  if (totalHours > 0) {
    let distributed = 0;
    for (let i = 0; i < entries.length - 1; i++) {
      entries[i].calculated_share = roundCents(netPool * entries[i].percent_of_hours);
      distributed += entries[i].calculated_share;
    }
    if (entries.length > 0) {
      entries[entries.length - 1].calculated_share = roundCents(netPool - distributed);
    }
  }

  for (const e of entries) {
    e.total = roundCents(e.calculated_share + e.extra_tips + e.event_tips_total);
  }

  return {
    total_tips: totalTips,
    processing_fee: processingFee,
    net_pool: netPool,
    total_hours: totalHours,
    tip_per_hour: roundCents(tipPerHour),
    entries,
  };
}

function roundCents(n: number): number {
  return Math.round(n * 100) / 100;
}
